import Backbone from 'backbone';
import * as d3 from 'd3';
import {debounce} from 'ts-debounce';
import _ from 'underscore';

import SecondaryStructureTabState from 'analyses/SecondaryStructureTabState';
import {
  FetchAllOligoSecondaryStructureResponse,
  FetchOligoSecondaryStructureResponse,
} from 'assembly/tabs/primer-assembly/PrimerAssemblyTypes';
import {PrimerPairCollection, SequencePrimer, SequencePrimerCollection} from 'BioJS';
import {
  MELTING_TEMP_INITIAL_PREFERENCES_DNA,
  MELTING_TEMP_INITIAL_PREFERENCES_RNA,
  NucleotideType,
} from 'biojs/BioConstants';
import {FragmentResult} from 'biojs/FragmentResult';
import * as util from 'biojs/Util';
import {Sequence} from 'components/Sequence';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {FindPrimersPreferences, PrimerPreferences} from 'constants/PrimerConstants';
import * as colors from 'lib/Colors';
import FindPrimersResults from 'molbio/find-primers/FindPrimersResultsCollection';
import {FindPrimersTabState} from 'molbio/find-primers/FindPrimersTabState';
import {TabState, TabStateDefaults} from 'Navigation';
import notifications from 'notifications';
import preferences from 'Preferences';
import * as TaskRunner from 'util/TaskRunner';

export interface PrimerAssemblyTabRowStateAttrs {
  fragmentSequenceID: string | null;
  fragmentSequenceName: string | null;
  fragmentResultColor: string | null;
  strand: StrandOfDoubleStranded | null;
  tmAnneal: number | null;
  tmWhole: number | null;
  tmDiff: number | null;
  binding: string | null;
  overhang: number | null;
  dg: number | null;
  misprime: boolean | null;
  pairedId: string | null;
  sequence: Sequence | null;
  fragmentResultIndex: number | null;
  pairId: string | null;
  dgHandler: () => void;
  misprimeHandler: () => void;
}

export class PrimerAssemblyTabRowState extends Backbone.Model {
  override initialize(): void {
    this.computeTm = this.computeTm.bind(this);
    this.computeDG = this.computeDG.bind(this);
    this.computeMisprime = this.computeMisprime.bind(this);
  }

  override defaults(): PrimerAssemblyTabRowStateAttrs {
    return {
      fragmentSequenceID: null,
      fragmentSequenceName: null,
      fragmentResultColor: null,
      strand: null,
      tmAnneal: null,
      tmWhole: null,
      tmDiff: null,
      binding: null,
      overhang: null,
      dg: null, // html
      misprime: null, // html
      pairedId: null,
      sequence: null,
      fragmentResultIndex: null,
      pairId: null,
      dgHandler() {
        return undefined;
      },
      misprimeHandler() {
        return undefined;
      },
    };
  }

  computeBinding(primer: SequencePrimer): void {
    this.set({binding: primer.bindingBases().length});
  }

  computeOverhang(primer: SequencePrimer): void {
    this.set({overhang: primer.overhangLength()});
  }

  computeTm(primer: SequencePrimer): void {
    const pairedPrimer = this.get('sequence').get('_sequence').sequencePrimers().get(this.get('pairedId'));
    const tmAnneal = util.meltingTemperature(primer.bindingBases(), NucleotideType.DNA);
    const tmWhole = util.meltingTemperature(primer.bases(), NucleotideType.DNA);
    const otherTm = util.meltingTemperature(pairedPrimer.bindingBases(), NucleotideType.DNA);
    const tmDiff = Math.abs(tmAnneal - otherTm);
    this.set({tmAnneal, tmWhole, tmDiff});
  }

  computeDG(primer: SequencePrimer): void {
    this.set({
      dg: null,
      dgHandler() {
        return undefined;
      },
    });
    let formData = new FormData();
    formData.append('nucleotideType', primer.nucleotideType());
    formData.append('primer', primer.bases());
    const val = preferences.get(PrimerPreferences.SECONDARY_TEMP);
    const temp = val != null && !Number.isNaN(parseInt(val, 10)) ? parseInt(val, 10) : 37;
    // @ts-expect-error FormData expects a string
    formData.append('temperature', temp);
    // @ts-expect-error FormData expects a string
    formData.append('skip_homodimer', true);
    TaskRunner.runTask({
      url: '/1/api/oligo-secondary-structure',
      data: formData,
      dataType: 'json',
      processData: false,
      contentType: false,
      type: 'PUT',
    })
      .fail(notifications.notifyError)
      .done((res: FetchOligoSecondaryStructureResponse) => {
        const dg = res.monomer.dg;
        const dgHandler = (): void => {
          this.set({dg: null});
          formData = new FormData();
          // @ts-expect-error FormData expects a string
          formData.append('all', true);
          formData.append('nucleotideType', primer.nucleotideType());
          formData.append('primer', primer.bases());
          // @ts-expect-error FormData expects a string
          formData.append('temperature', temp);
          const xhr = TaskRunner.runTask({
            url: '/1/api/oligo-secondary-structure',
            data: formData,
            dataType: 'json',
            processData: false,
            contentType: false,
            type: 'PUT',
          });
          xhr.fail(notifications.notifyError).done((allRes: FetchAllOligoSecondaryStructureResponse) => {
            SecondaryStructureTabState.create(
              this.get('sequence'),
              res.monomer,
              allRes,
              'Monomer',
              temp,
              primer.nucleotideType(),
              primer.bases()
            );
            this.set({dg});
          });
        };
        this.set({dg, dgHandler});
      });
  }

  computeMisprime(primer: SequencePrimer): void {
    const sequence = this.get('sequence');
    const _sequence = sequence.get('_sequence');
    if (_sequence.finalized()) {
      return;
    }

    this.set({
      misprime: null,
      misprimeHandler() {
        return undefined;
      },
    });

    const fragmentResult = _sequence.fragmentResults().at(this.get('fragmentResultIndex'));
    let data = {
      bases: primer.bases(),
      dnaTmPreferences: MELTING_TEMP_INITIAL_PREFERENCES_DNA,
      rnaTmPreferences: MELTING_TEMP_INITIAL_PREFERENCES_RNA,
      nucleotideType: NucleotideType.DNA,
      min_matching_bases: preferences.get(FindPrimersPreferences.MIN_MATCHING_BASES),
      min_temp: preferences.get(FindPrimersPreferences.MIN_MELTING_TEMP),
      max_temp: preferences.get(FindPrimersPreferences.MAX_MELTING_TEMP),
      min_base_index: fragmentResult.start(),
      max_base_index: fragmentResult.end(),
      max_mismatches: preferences.get(FindPrimersPreferences.MAX_MISMATCHES),
      max_total_mismatches: preferences.get(FindPrimersPreferences.MAX_TOTAL_MISMATCHES),
      exact_match: false,
      unique_match: false,
    };

    const findPrimersResults = new FindPrimersResults([], {sequence_id: _sequence.id});
    findPrimersResults
      .fetch({data})
      .fail(notifications.notifyError)
      .done(() => {
        const misprimeHandler = function (): void {
          data = {
            // @ts-expect-error shadows/clobbers outer data
            findType: 3,
            searchBases: primer.bases(),
            minBaseIndex: fragmentResult.start(),
            maxBaseIndex: fragmentResult.end(),
            exactMatch: false,
            uniqueMatch: false,
          };
          FindPrimersTabState.insert(sequence, data);
        };
        this.set({misprime: findPrimersResults.length > 1, misprimeHandler});
      });
  }
}

class PrimerAssemblyTabRowStates extends Backbone.Collection<PrimerAssemblyTabRowState> {
  static initClass(): void {
    this.prototype.model = PrimerAssemblyTabRowState;
  }
}

PrimerAssemblyTabRowStates.initClass();

interface PrimerAssemblyTabStateAttrs extends TabStateDefaults {
  showPrintableSummary: boolean;
}

class PrimerAssemblyTabState extends TabState {
  primers!: SequencePrimerCollection;
  primerPairs!: PrimerPairCollection;

  override defaults(): PrimerAssemblyTabStateAttrs {
    // eslint-disable-next-line no-restricted-syntax
    return _.extend(super.defaults(), {
      title: 'Assembly Parameters',
      closeable: false,
      showPrintableSummary: false,
      preferenceKey: 'primer-assembly',
    });
  }

  override initialize(): void {
    const _sequence = this.get('sequence').get('_sequence');
    this.primerPairs = _sequence.primerPairs();
    this.primers = _sequence.sequencePrimers();
    this.set({rows: new PrimerAssemblyTabRowStates()});

    this.listenTo(_sequence, 'change:sequencePrimers', () => {
      if (this.primers != null) {
        this.stopListening(this.primers);
      }
      this.primers = _sequence.sequencePrimers();
      this.listenToPrimers(this.primers);
    });
    this.listenToPrimers(this.primers);

    // Primers never change unless the sequence bases change. This could probably be formalized
    // somehow though.
    this.listenTo(_sequence, 'change:bases', () => {
      const rows: PrimerAssemblyTabRowStates = this.get('rows');
      for (const row of rows.models) {
        row.computeMisprime(this.primers.get(row.id));
      }
    });

    this.listenToPrimerPairs(this.primerPairs);
    this.listenTo(_sequence, 'change:primerPairs', () => {
      if (this.primerPairs != null) {
        this.stopListening(this.primerPairs);
      }
      this.primerPairs = _sequence.primerPairs();
      this.listenToPrimerPairs(this.primerPairs);
      this.computeRows();
    });

    this.listenTo(preferences, `change:${PrimerPreferences.MELTING_TEMP_DNA}`, function () {
      // @ts-expect-error shadows outer this
      return this.allRows((row) => row.computeTm);
    });
    const changes = [
      'change:findprimers.MIN_MATCHING_BASES',
      'change:findprimers.MIN_MELTING_TEMP',
      'change:findprimers.MAX_MELTING_TEMP',
      'change:findprimers.MAX_MISMATCHES',
    ].join(' ');
    this.listenTo(preferences, changes, function () {
      // @ts-expect-error shadows outer this
      return this.allRows((row) => row.computeMisprime);
    });
    const computeDG = (): void => {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.allRows((row) => row.computeDG);
    };
    this.listenTo(preferences, `change:${PrimerPreferences.SECONDARY_TEMP}`, debounce(computeDG, 500));

    this.computeRows();
  }

  showPrintableSummary(): boolean {
    return this.get('showPrintableSummary');
  }

  allRows(rowFn: (row: PrimerAssemblyTabRowState) => (primer: SequencePrimer) => void): void {
    for (const primer of this.primers.models) {
      const row = this.get('rows').get(primer.id);
      if (row != null) {
        rowFn(row)(primer);
      }
    }
  }

  computeRows(): void {
    const rows = this.get('rows');
    const newRows: Array<PrimerAssemblyTabRowState> = [];
    const _sequence = this.get('sequence').get('_sequence');
    // @ts-expect-error Not all code paths return a result
    _sequence.fragmentResults().each((fragmentResult: FragmentResult, i: number) => {
      const color = colors.shuffledPastelColors[i];
      const pair = this.primerPairs.get(fragmentResult.primerPairID()!);
      if (pair != null) {
        const [fwd, rev]: [SequencePrimer, SequencePrimer] = pair.getPrimers(this.primers);
        const result = [];
        for (const [primer, other] of [
          [fwd, rev],
          [rev, fwd],
        ]) {
          const row = new PrimerAssemblyTabRowState({
            id: primer.id,
            fragmentResultColor: color,
            primerName: primer.name(),
            strand: primer.strand(),
            pairedId: other.id,
            sequence: this.get('sequence'),
            fragmentResultIndex: i,
            pairId: pair.id,
          });
          row.computeBinding(primer);
          row.computeOverhang(primer);
          row.computeTm(primer);
          row.computeDG(primer);
          if (!_sequence.finalized()) {
            row.computeMisprime(primer);
          }
          result.push(newRows.push(row));
        }
        return result;
      }
    });
    rows.reset(newRows);
  }

  listenToPrimers(primers: SequencePrimerCollection): void {
    const computePairedTm = (row: PrimerAssemblyTabRowState): void => {
      const pairedId = row.get('pairedId');
      const pairedPrimer = this.get('sequence').get('_sequence').sequencePrimers().get(pairedId);
      const pairedRow = this.get('rows').get(pairedId);
      pairedRow.computeTm(pairedPrimer);
    };
    this.listenTo(primers, 'change:bases', function (primer: SequencePrimer): void {
      // @ts-expect-error shadows outer this
      const row = this.get('rows').get(primer.id);
      if (row != null) {
        row.computeBinding(primer);
        row.computeTm(primer);
        row.computeDG(primer);
        row.computeMisprime(primer);
        computePairedTm(row);
      }
    });
    this.listenTo(primers, 'change:overhang_length', function (primer: SequencePrimer): void {
      // @ts-expect-error shadows outer this
      const row = this.get('rows').get(primer.id);
      if (row != null) {
        row.computeBinding(primer);
        row.computeOverhang(primer);
        row.computeTm(primer);
        computePairedTm(row);
      }
    });
    this.listenTo(primers, 'change:name', function (primer: SequencePrimer): void {
      // @ts-expect-error shadows outer this
      const row = this.get('rows').get(primer.id);
      if (row != null) {
        row.set({primerName: primer.name()});
      }
    });
  }

  listenToPrimerPairs(pairs: PrimerPairCollection): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(pairs, 'remove reset', this.computeRows);
  }

  lighten(fragmentResultColor: string): string {
    if (this.showPrintableSummary()) {
      return fragmentResultColor;
    }
    const rgb = d3.rgb(fragmentResultColor);
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.4)`;
  }
}

export default PrimerAssemblyTabState;
